// Helpers for a site's own form inputs (`field_components` and `custom_fields` of useFormRenderer / useFormSteps / useWidgetFormRenderer),
// so every site draws its inputs from the field and core instead of writing these again. Added for jument (form builder forms on
// widgets and the seller request); nothing in core calls them except the props the renderer gives a site's file and typed inputs.
// Pure, so it runs under `node --test`. Docs: documents/helpers/formBuilderSiteHelpers.md
import { lang_of } from "./messages.js";

// ---- digits --------------------------------------------------------------------------------------------------------

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

// Persian and Arabic digits -> Latin, everything else kept ("۰۹۱۲abc" -> "0912abc")
export const to_latin_digits = (value) => String(value ?? "").replace(/[۰-۹٠-٩]/g, (char) => {
    const index = PERSIAN_DIGITS.indexOf(char);
    return String(index >= 0 ? index : ARABIC_DIGITS.indexOf(char));
});

// national code and postal code are 10 digits
const TEN_DIGIT_TYPES = ["national_code", "postal_code"];
// an admin pattern that takes digits only: ^\d{11}$ or ^\d{1,20}$
const DIGITS_ONLY_REGEX = /^\^\\d\{(\d+)(?:,(\d+))?\}\$$/;

// How many digits a field takes when it takes digits only, or null when it is free text: the 10-digit codes, a text field whose
// pattern is digits only (its upper bound), a whole number with a non-negative min and a max (the digits of the max).
export const digits_limit = (field) => {
    if (TEN_DIGIT_TYPES.includes(field?.type)) {
        return 10;
    }
    const match = typeof field?.data?.regex === "string" ? field.data.regex.match(DIGITS_ONLY_REGEX) : null;
    if (match) {
        return Number(match[2] || match[1]);
    }
    if (field?.type === "number" && field.data?.float !== true && Number.isInteger(field.data?.max) && (field.data?.min ?? 0) >= 0) {
        return String(field.data.max).length;
    }
    return null;
};

// ---- files ---------------------------------------------------------------------------------------------------------

const MIME_EXTENSIONS = Object.freeze({
    "image/jpeg": ["jpg", "jpeg"],
    "image/png": ["png"],
    "image/webp": ["webp"],
    "image/gif": ["gif"],
    "image/*": ["jpg", "jpeg", "png", "webp", "gif"],
    "application/pdf": ["pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": ["docx"],
});

// an `accept` text (extensions and/or mime types) -> its extensions: "image/png,.pdf" -> ["png", "pdf"]
export const accept_extensions = (accept) => [...new Set(String(accept ?? "").split(",")
    .map((item) => item.trim().toLowerCase())
    .filter(Boolean)
    .flatMap((item) => (item.startsWith(".") ? [item.slice(1)] : (MIME_EXTENSIONS[item] || []))))];

// the formats people read: "image/png,image/jpeg" -> ["PNG", "JPG"] (jpeg is shown as JPG once)
export const format_names = (accept) => [...new Set(accept_extensions(accept).map((extension) => (extension === "jpeg" ? "JPG" : extension.toUpperCase())))];

const join_list = (items, lang) => {
    if (items.length <= 1) {
        return items.join("");
    }
    const [separator, last] = lang === "en" ? [", ", " or "] : ["، ", " یا "];
    return `${items.slice(0, -1).join(separator)}${last}${items[items.length - 1]}`;
};

const size_text = (bytes, lang) => {
    const megabytes = bytes >= 1024 * 1024;
    const amount = megabytes ? bytes / (1024 * 1024) : Math.max(1, Math.round(bytes / 1024));
    if (lang === "en") {
        return `up to ${Math.round(amount * 10) / 10} ${megabytes ? "MB" : "KB"}`;
    }
    return `حداکثر ${amount.toLocaleString("fa-IR", { maximumFractionDigits: 1 })} ${megabytes ? "مگابایت" : "کیلوبایت"}`;
};

// The line under a file input when the admin wrote no hint: the formats and the size the field takes.
// file_note("image/png,image/jpeg,image/webp", 2 MB, "fa") -> "PNG، JPG یا WEBP؛ حداکثر ۲ مگابایت"; "en" -> "PNG, JPG or WEBP; up to 2 MB"
export const file_note = (accept, max_file_bytes, lang) => {
    const language = lang_of(lang);
    const parts = [];
    const names = format_names(accept);
    if (names.length > 0) {
        parts.push(join_list(names, language));
    }
    if (Number.isFinite(max_file_bytes) && max_file_bytes > 0) {
        parts.push(size_text(max_file_bytes, language));
    }
    return parts.join(language === "en" ? "; " : "؛ ");
};

// ---- texts ---------------------------------------------------------------------------------------------------------

const is_plain_object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

// Texts for the `messages` option, one source over the other (`{ fa: { errors: {...}, labels: {...} } }` or the flat
// `{ errors, labels }`): every level of plain objects is merged, a later source wins. Pass module constants and keep the
// result a module constant too (or memoise it): a new object on every render redraws the form.
export const merge_messages = (...sources) => sources.reduce((result, source) => {
    if (!is_plain_object(source)) {
        return result;
    }
    const merged = { ...result };
    Object.entries(source).forEach(([key, value]) => {
        merged[key] = is_plain_object(value) && is_plain_object(merged[key]) ? merge_messages(merged[key], value) : value;
    });
    return merged;
}, {});

// ---- Iranian bank card, IBAN (sheba) and Instagram id ----------------------------------------------------------------
// For a site's custom fields (`custom:card-number`...): normalise what is typed with these, and give the rules in `custom_rules`
// under the site's own custom field keys: `custom_rules: { "card-number": IRANIAN_FIELD_RULES.card_number }`. Core has texts for
// the codes they return (invalid_card_number, invalid_sheba_number, invalid_instagram_id); a site may word them itself in `messages`.

// a 16-digit card number with a valid Luhn check digit
export const is_valid_card_number = (value) => {
    const text = String(value ?? "");
    if (!/^\d{16}$/.test(text)) {
        return false;
    }
    let sum = 0;
    for (let i = 0; i < 16; i += 1) {
        let digit = Number(text[15 - i]);
        if (i % 2 === 1) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }
        sum += digit;
    }
    return sum % 10 === 0;
};
// IR + 24 digits whose IBAN check (mod 97) holds
export const is_valid_sheba = (value) => {
    const text = String(value ?? "").toUpperCase();
    if (!/^IR\d{24}$/.test(text)) {
        return false;
    }
    // move "IR" and the two check digits to the end, letters become numbers (I = 18, R = 27), then mod 97 must be 1
    const rearranged = `${text.slice(4)}1827${text.slice(2, 4)}`;
    let remainder = 0;
    for (const char of rearranged) {
        remainder = (remainder * 10 + Number(char)) % 97;
    }
    return remainder === 1;
};

export const is_valid_instagram_id = (value) => /^@?[A-Za-z0-9._]{1,30}$/.test(String(value ?? ""));

// what is typed -> the digits of a card number, at most 16
export const normalize_card_number = (raw) => to_latin_digits(raw).replace(/\D/g, "").slice(0, 16);

// the 24 digits of an IBAN, without IR (what the input shows when IR is drawn beside it)
export const sheba_digits = (raw) => to_latin_digits(raw).replace(/^\s*IR/i, "").replace(/\D/g, "").slice(0, 24);

// what is typed -> the value kept: "IR" + the digits, or "" when there are none
export const normalize_sheba = (raw) => {
    const digits = sheba_digits(raw);
    return digits ? `IR${digits}` : "";
};

// an Instagram id: spaces, a page link, a leading @, a query and a trailing slash are taken off
export const normalize_instagram_id = (raw) => to_latin_digits(raw)
    .replace(/\s+/g, "")
    .replace(/^(https?:\/\/)?(www\.)?instagram\.com\//i, "")
    .replace(/^@+/, "")
    .split("?")[0]
    .replace(/\/+$/, "");

export const IRANIAN_FIELD_RULES = Object.freeze({
    card_number: (value) => (is_valid_card_number(value) ? null : "invalid_card_number"),
    // two faults the visitor can tell apart: digits missing (sheba_length), or 24 digits that are not a real IBAN (the mod 97
    // check). The digits are judged with IR in front, so a value kept with or without IR is read the same.
    sheba_number: (value) => {
        const digits = sheba_digits(value);
        if (digits.length !== 24) {
            return "sheba_length";
        }
        return is_valid_sheba(`IR${digits}`) ? null : "invalid_sheba_number";
    },
    instagram_id: (value) => (is_valid_instagram_id(value) ? null : "invalid_instagram_id"),
});
